import { type FC, type ReactNode, useCallback } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Dialog, DialogContent } from '@mui/material'
import { CloseButton } from '@/common/components'

interface WizardModalProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly children: ReactNode
  readonly maxWidth?: 'xs' | 'sm' | 'md'
  readonly hideCloseButton?: boolean
  readonly ariaLabel?: string
}

export const WizardModal: FC<WizardModalProps> = ({
  open,
  onClose,
  children,
  maxWidth = 'sm',
  hideCloseButton = false,
  ariaLabel = 'Wizard',
}) => {
  const handleDialogClose = useCallback(
    (_event: unknown, reason: string) => {
      if (reason === 'backdropClick') return
      onClose()
    },
    [onClose],
  )

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth={maxWidth}
      fullWidth
      aria-label={ariaLabel}
      sx={wizardDialogSx}
    >
      {!hideCloseButton && <CloseButton onClick={onClose} sx={wizardCloseButtonSx} />}
      <DialogContent sx={wizardContentSx}>{children}</DialogContent>
    </Dialog>
  )
}

const wizardDialogSx: SxProps<Theme> = {
  '& .MuiDialog-paper': {
    borderRadius: 2,
    position: 'relative',
  },
}

const wizardCloseButtonSx: SxProps<Theme> = {
  position: 'absolute',
  right: 12,
  top: 12,
}

const wizardContentSx: SxProps<Theme> = {
  pt: 5,
  pb: 3,
  px: 3,
}
